import type { Dispatch, MutableRefObject } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  createWorkspace,
  getLibraries,
  getWorkspaces,
  type InventoryResolveEvent,
  type ResolvedInventoryDTO,
  streamLibraryInventory,
} from "../../api/client";
import type { Library, Workspace } from "../../types";
import { diffLibraryInventory } from "../libraryDiff";
import { isTargetDoctype } from "../libraryDoctypeVocabulary";
import { getCachedDocId, scheduleSave } from "../services/documentSessionService";
import { getLibraryDoctype } from "../services/libraryDoctypeStore";
import { pickInitialWorkspace } from "../services/workspacePref";
import type { VariantCEvent } from "../state";
import { shortHash } from "../utils/spineSnapshot";

export interface UseWorkspaceLibraryParams {
  sessionLoading: boolean;
  sessionWorkspaceRef: MutableRefObject<string | null>;
  /** selectedLibraryId stays in the shell (owned there for bootstrap);
   *  the hook reads it to fire refreshLibraryInventory. */
  selectedLibraryId: string;
  /** Called when library select should be cleared (cross-workspace switch). */
  setSelectedLibraryId: (id: string) => void;
  selectedDocCode: string | null;
  /** activeSelection?.docCode — secondary docCode source for inventory. */
  activeDocCode: string | null | undefined;
  runId: string | null;
  dispatch: Dispatch<VariantCEvent>;
  userIdentifier: string | null | undefined;
  /** Restored from DocumentSessionDoc on bootstrap. When present and
   *  the (libraryId, doctype, snapshot_hash) key matches the next
   *  refresh would resolve, the hook seeds inventory from this snapshot
   *  and skips the SSE re-stream entirely. */
  restoredInventorySnapshot?: ResolvedInventoryDTO | null;
  restoredInventoryDoctype?: string | null;
}

export interface UseWorkspaceLibraryResult {
  workspaces: Workspace[];
  selectedWorkspaceId: string | null;
  libraries: Library[];
  workspaceFetchError: string | null;
  libraryInventory: ResolvedInventoryDTO | null;
  libraryInventoryLoading: boolean;
  libraryResolveEvent: InventoryResolveEvent | null;
  /** Re-fetch the workspace's library list. Call after a mutation that
   *  adds/removes a library (e.g. create) so it shows up immediately
   *  without a workspace switch or a full re-login. */
  refreshLibraries: () => Promise<void>;
  refreshLibraryInventory: () => Promise<void>;
  /** Clear the (libraryId, doctype) skip-guard so the next call to
   *  refreshLibraryInventory always re-streams. Callers fire this on
   *  mutations that change the library snapshot (upload, delete,
   *  reclassify) so the cached resolve no longer survives. */
  invalidateResolveGuard: () => void;
  /** Optimistically drop a doc from the in-memory inventory so the
   *  Hub list updates immediately on delete, without waiting for the
   *  full SSE re-resolve (which can take seconds). */
  removeDocumentFromInventory: (docId: string) => void;
  handleSelectedWorkspaceIdChange: (id: string) => void;
  /** Create a custom (billable) workspace and switch to it. Backs the
   *  WorkspacePicker "+ New workspace" affordance. */
  createAndSelectWorkspace: (name: string) => Promise<void>;
}

export function useWorkspaceLibrary({
  sessionLoading,
  sessionWorkspaceRef,
  selectedLibraryId,
  setSelectedLibraryId,
  selectedDocCode,
  activeDocCode,
  runId,
  dispatch,
  userIdentifier,
  restoredInventorySnapshot,
  restoredInventoryDoctype,
}: UseWorkspaceLibraryParams): UseWorkspaceLibraryResult {
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState<string | null>(null);
  const [libraries, setLibraries] = useState<Library[]>([]);
  const [workspaceFetchError, setWorkspaceFetchError] = useState<string | null>(null);
  const [libraryInventory, setLibraryInventory] = useState<ResolvedInventoryDTO | null>(
    restoredInventorySnapshot ?? null,
  );
  const [libraryInventoryLoading, setLibraryInventoryLoading] = useState(false);
  const [libraryResolveEvent, setLibraryResolveEvent] = useState<InventoryResolveEvent | null>(null);
  // Tracks last-observed library snapshot hash; used to detect
  // library-content changes between refreshes and emit a library event.
  const lastLibraryHashRef = useRef<{
    libraryId: string;
    hash: string;
    inventory: ResolvedInventoryDTO;
  } | null>(
    restoredInventorySnapshot
      ? {
          libraryId: restoredInventorySnapshot.library_id,
          hash: restoredInventorySnapshot.library_snapshot_hash,
          inventory: restoredInventorySnapshot,
        }
      : null,
  );
  // Guard: skip the SSE stream when (libraryId, doctype) already
  // resolved. Seeded from the bootstrap snapshot so the very first
  // refresh after restore is a no-op when nothing has changed. The
  // hash is intentionally NOT part of the skip key — we don't yet
  // know the current server hash; the snapshot match is purely
  // (libraryId, doctype). A fresh upload/delete invalidates via the
  // explicit ``invalidateResolveGuard`` path (called by LibraryHub).
  const lastResolvedKeyRef = useRef<string | null>(
    restoredInventorySnapshot && restoredInventoryDoctype
      ? `${restoredInventorySnapshot.library_id}|${restoredInventoryDoctype}`
      : null,
  );
  // Single-slot in-flight stream tracker. When the effect re-fires for
  // a different (libraryId, doctype) pair (e.g. user picks a doctype
  // mid-stream), abort the prior stream before starting the new one
  // so the SSE drains can't overwrite newer state. Same key → no-op.
  const inFlightRef = useRef<{ key: string; controller: AbortController } | null>(null);
  // Track previous library ID to detect library switches and clear stale inventory.
  const prevLibraryIdRef = useRef<string>("");

  // ----- Workspace fetch (Spec 23 W1) ------------------------------------
  // Fetch workspaces once on mount; pick initial via persisted
  // preference (falls back to first if pref is stale or missing).
  // Gated on sessionLoading so the session's workspace_id is available
  // before pickInitialWorkspace runs.
  useEffect(() => {
    if (sessionLoading) return;
    let cancelled = false;
    (async () => {
      try {
        const ws = await getWorkspaces();
        if (cancelled) return;
        setWorkspaces(ws);
        const preference = sessionWorkspaceRef.current;
        const pick = pickInitialWorkspace(ws, preference);
        setSelectedWorkspaceId(pick.id);
      } catch (err) {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : String(err);
        console.warn("[useWorkspaceLibrary] getWorkspaces failed:", err);
        setWorkspaceFetchError(message);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [sessionLoading, sessionWorkspaceRef]);

  // ----- Libraries re-fetch on workspace change -------------------------
  // Clears the prior library selection so a stale library id doesn't
  // cross workspace boundaries (libraries are workspace-scoped).
  // biome-ignore lint/correctness/useExhaustiveDependencies: selectedLibraryId intentionally omitted
  useEffect(() => {
    if (!selectedWorkspaceId) {
      setLibraries([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const libs = await getLibraries(selectedWorkspaceId);
        if (cancelled) return;
        setLibraries(libs);
        // If the prior selectedLibraryId isn't in the new list, clear
        // it. Workspace switch invalidates any prior library context.
        if (selectedLibraryId && !libs.some((l) => l.id === selectedLibraryId)) {
          setSelectedLibraryId("");
        }
      } catch (err) {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : String(err);
        console.warn("[useWorkspaceLibrary] getLibraries failed:", err);
        setWorkspaceFetchError(message);
      }
    })();
    return () => {
      cancelled = true;
    };
    // selectedLibraryId intentionally omitted — including it would
    // re-fire on every library change, undoing the cross-workspace
    // clear. Workspace change is the only trigger we care about.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedWorkspaceId, setSelectedLibraryId]);

  // Re-fetch the library list for the active workspace. Used both by
  // the workspace-change effect above (indirectly) and, exposed on the
  // result, by callers that mutate the library set (create) so the new
  // library appears without a re-login. Kept separate from the effect's
  // inline fetch because it deliberately skips the cross-workspace
  // ``clear selectedLibraryId`` logic — a create shouldn't drop the
  // selection.
  const refreshLibraries = useCallback(async () => {
    if (!selectedWorkspaceId) return;
    try {
      const libs = await getLibraries(selectedWorkspaceId);
      setLibraries(libs);
    } catch (err) {
      console.warn("[useWorkspaceLibrary] refreshLibraries failed:", err);
    }
  }, [selectedWorkspaceId]);

  // ----- Library inventory refresh (Spec 23 P2.2 + Spec 24 T2) ---------
  const refreshLibraryInventory = useCallback(async () => {
    if (!selectedLibraryId) {
      // Cancel any in-flight stream when the library is cleared.
      if (inFlightRef.current) {
        inFlightRef.current.controller.abort();
        inFlightRef.current = null;
      }
      setLibraryInventory(null);
      setLibraryResolveEvent(null);
      lastLibraryHashRef.current = null;
      lastResolvedKeyRef.current = null;
      return;
    }
    // Prefer the live picker selection as target_doctype; fall back
    // to the per-library localStorage entry. ``selectedDocCode`` is
    // the live picker value (set by DoctypePicker before any draft
    // commits); ``activeDocCode`` only lands after handleStartDraft.
    const liveDocCode = selectedDocCode ?? activeDocCode ?? null;
    const pickerDoctype = liveDocCode && isTargetDoctype(liveDocCode) ? liveDocCode : null;
    const targetDoctype = pickerDoctype ?? getLibraryDoctype(selectedLibraryId);
    // Skip the SSE re-stream when we've already resolved this
    // (libraryId, doctype) pair AND we still hold a matching
    // inventory in memory. This is what prevents the title-selector
    // re-resolve loop: /draft -> DoctypePicker -> setSelectedDocCode
    // would otherwise re-fire the effect for the same library.
    const nextKey = `${selectedLibraryId}|${targetDoctype ?? ""}`;
    const cached = lastLibraryHashRef.current;
    const haveLiveInventory = Boolean(cached && cached.libraryId === selectedLibraryId);
    if (lastResolvedKeyRef.current === nextKey && haveLiveInventory) {
      return;
    }
    // Serialize: if an earlier stream is still in flight, either
    // it's for the same key (let it finish — duplicate call) or it's
    // for a different key (abort it, then start the new one). The
    // user's explicit intent is "run them in sequence" — cancel and
    // restart, never race two SSE consumers into the same UI state.
    if (inFlightRef.current) {
      if (inFlightRef.current.key === nextKey) {
        return;
      }
      inFlightRef.current.controller.abort();
      inFlightRef.current = null;
    }
    const controller = new AbortController();
    const myEntry = { key: nextKey, controller };
    inFlightRef.current = myEntry;
    const isCurrent = () => inFlightRef.current === myEntry;
    setLibraryInventoryLoading(true);
    setLibraryResolveEvent(null);
    try {
      // Streaming variant — surfaces per-doc progress via SSE so the
      // chat-surface LibraryBar can show "Classifying X…". The non-
      // streaming ``getLibraryInventory`` is still exported for any
      // caller that doesn't care about progress.
      const inv = await streamLibraryInventory(
        selectedLibraryId,
        targetDoctype,
        (event) => {
          if (!isCurrent()) return;
          setLibraryResolveEvent(event);
        },
        controller.signal,
      );
      // A newer call has superseded us — drop the result on the floor.
      if (!isCurrent()) return;
      setLibraryInventory(inv);
      lastResolvedKeyRef.current = nextKey;
      // Persist the resolution onto the DocumentSessionDoc so the
      // next reopen can hydrate from it and skip the stream.
      const docId = getCachedDocId();
      if (docId) {
        scheduleSave(docId, {
          library_inventory_snapshot: inv,
          library_snapshot_hash: inv.library_snapshot_hash ?? null,
          library_resolved_doctype: targetDoctype ?? null,
          last_modified_by: userIdentifier ?? undefined,
        });
      }
      // Spec 23 P2.2 — detect library content change between refreshes.
      // Only dispatches when a previous hash is known (skips the first
      // observation per library to avoid a spurious "swap" on mount).
      const nextHash = inv?.library_snapshot_hash;
      if (nextHash) {
        const prev = lastLibraryHashRef.current;
        if (prev && prev.libraryId === selectedLibraryId && prev.hash !== nextHash) {
          // Spec 24 T2 — compute the per-document diff.
          const diff = diffLibraryInventory(prev.inventory, inv);
          dispatch({
            type: "EVENT_APPENDED",
            entry: {
              ts: Date.now(),
              actor: "you",
              sectionId: null,
              runId,
              payload: {
                kind: "library",
                fromVersion: shortHash(prev.hash),
                toVersion: shortHash(nextHash),
                diff: diff.length > 0 ? diff : undefined,
              },
            },
          });
        }
        lastLibraryHashRef.current = {
          libraryId: selectedLibraryId,
          hash: nextHash,
          inventory: inv,
        };
      }
    } catch (err) {
      // Aborted by a newer call — silent. Anything else gets logged
      // and clears the inventory so the UI doesn't keep stale data.
      if (controller.signal.aborted) return;
      console.warn("[useWorkspaceLibrary] inventory fetch failed:", err);
      if (isCurrent()) setLibraryInventory(null);
    } finally {
      if (isCurrent()) {
        inFlightRef.current = null;
        setLibraryInventoryLoading(false);
        setLibraryResolveEvent(null);
      }
    }
  }, [selectedLibraryId, runId, selectedDocCode, activeDocCode, dispatch, userIdentifier]);

  // Clear inventory when switching libraries to prevent showing stale data
  // from the previous library while the new library's classification is loading.
  useEffect(() => {
    if (selectedLibraryId && prevLibraryIdRef.current !== selectedLibraryId) {
      setLibraryInventory(null);
    }
    prevLibraryIdRef.current = selectedLibraryId;
  }, [selectedLibraryId]);

  useEffect(() => {
    refreshLibraryInventory();
  }, [refreshLibraryInventory]);

  // Abort any in-flight stream on unmount so the SSE reader doesn't
  // keep pumping events into a dead component.
  useEffect(() => {
    return () => {
      if (inFlightRef.current) {
        inFlightRef.current.controller.abort();
        inFlightRef.current = null;
      }
    };
  }, []);

  const invalidateResolveGuard = useCallback(() => {
    lastResolvedKeyRef.current = null;
  }, []);

  const removeDocumentFromInventory = useCallback((docId: string) => {
    setLibraryInventory((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        classifications: prev.classifications.filter((c) => c.doc_id !== docId),
        source_classifications: prev.source_classifications?.filter((c) => c.doc_id !== docId),
      };
    });
  }, []);

  // ----- Workspace-change handler (Spec 23 W2) --------------------------
  const handleSelectedWorkspaceIdChange = useCallback(
    (id: string) => {
      setSelectedWorkspaceId(id);
      scheduleSave(getCachedDocId(), {
        workspace_id: id,
        last_modified_by: userIdentifier ?? undefined,
      });
    },
    [userIdentifier],
  );

  // ----- Create a custom (billable) workspace, then switch to it ---------
  // A custom workspace is where a paid draft's custom library lives (the demo
  // workspace stays free). Adds the new workspace to the list and selects it so
  // the caller (WorkspacePicker "+ New workspace") lands the writer in it.
  const createAndSelectWorkspace = useCallback(
    async (name: string) => {
      const ws = await createWorkspace(name);
      setWorkspaces((prev) => (prev.some((w) => w.id === ws.id) ? prev : [...prev, ws]));
      handleSelectedWorkspaceIdChange(ws.id);
    },
    [handleSelectedWorkspaceIdChange],
  );

  return {
    workspaces,
    selectedWorkspaceId,
    libraries,
    workspaceFetchError,
    libraryInventory,
    libraryInventoryLoading,
    libraryResolveEvent,
    refreshLibraries,
    refreshLibraryInventory,
    invalidateResolveGuard,
    removeDocumentFromInventory,
    handleSelectedWorkspaceIdChange,
    createAndSelectWorkspace,
  };
}
